import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment,
  Stack, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import GiphyAttribution from './GiphyAttribution';

// Waiting for a pause in typing keeps searches within GIPHY's hourly quota.
export const SEARCH_DEBOUNCE_MS = 1500;
export const GIF_UNAVAILABLE_MESSAGE = 'GIFs are taking a breather right now. Please try again in a few minutes.';

/**
 * GIPHY picker: trending GIFs by default, debounced search, and "Load more" (20 at a time).
 * `onSelect(gif)` receives `{ id, title, width, height, url, previewUrl }`.
 */
const GifPickerDialog = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [gifs, setGifs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Ignores responses for a search the user has already moved past.
  const requestRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    requestRef.current += 1;
    const requestId = requestRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setGifs([]);
    setHasMore(false);
    unifiedApi.getChatGifs({ query: debouncedQuery })
      .then((page) => {
        if (requestRef.current !== requestId) return;
        setGifs(page?.gifs || []);
        setHasMore(Boolean(page?.hasMore));
        setNextOffset(page?.nextOffset || 0);
      })
      .catch(() => {
        if (requestRef.current === requestId) setError(GIF_UNAVAILABLE_MESSAGE);
      })
      .finally(() => {
        if (requestRef.current === requestId) setLoading(false);
      });
  }, [open, debouncedQuery]);

  const loadMore = async () => {
    const requestId = requestRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await unifiedApi.getChatGifs({ query: debouncedQuery, offset: nextOffset });
      if (requestRef.current !== requestId) return;
      setGifs((previous) => {
        const seen = new Set(previous.map((gif) => gif.id));
        return [...previous, ...(page?.gifs || []).filter((gif) => !seen.has(gif.id))];
      });
      setHasMore(Boolean(page?.hasMore));
      if (page?.nextOffset) setNextOffset(page.nextOffset);
    } catch {
      if (requestRef.current === requestId) setError(GIF_UNAVAILABLE_MESSAGE);
    } finally {
      if (requestRef.current === requestId) setLoadingMore(false);
    }
  };

  const choose = (gif) => {
    onSelect(gif);
    onClose();
  };

  const searching = debouncedQuery.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      aria-labelledby="gif-picker-title"
    >
      <DialogTitle id="gif-picker-title" sx={{ pr: 6 }}>
        Choose a GIF
        <IconButton onClick={onClose} aria-label="Close GIF picker" sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: { sm: '70vh' } }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search GIPHY"
          inputProps={{ 'aria-label': 'Search GIFs', maxLength: 50 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
        />
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5, wordBreak: 'break-word' }}>
          {searching ? `Results for “${debouncedQuery}”` : 'Trending'}
        </Typography>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label="Loading GIFs" />
            </Box>
          )}

          {!loading && error && gifs.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 6, px: 2 }} role="status">
              {error}
            </Typography>
          )}

          {!loading && !error && gifs.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 6, px: 2, wordBreak: 'break-word' }} role="status">
              {searching ? `No GIFs found for “${debouncedQuery}”.` : 'No GIFs to show right now.'}
            </Typography>
          )}

          {!loading && gifs.length > 0 && (
            <Box
              role="list"
              aria-label="GIFs"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 1,
              }}
            >
              {gifs.map((gif) => (
                <Box key={gif.id} role="listitem">
                  <ButtonBase
                    onClick={() => choose(gif)}
                    aria-label={`Choose GIF: ${gif.title || 'Untitled'}`}
                    sx={{
                      display: 'block', width: '100%', aspectRatio: '4 / 3', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200',
                    }}
                  >
                    <Box
                      component="img"
                      src={gif.previewUrl}
                      alt={gif.title || 'GIF'}
                      loading="lazy"
                      sx={{
                        display: 'block', width: '100%', height: '100%', objectFit: 'cover',
                      }}
                    />
                  </ButtonBase>
                </Box>
              ))}
            </Box>
          )}

          {!loading && error && gifs.length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>{error}</Alert>
          )}
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <GiphyAttribution sx={{ textAlign: { xs: 'center', sm: 'left' } }} />
          {!loading && hasMore && (
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loadingMore}
              fullWidth={isMobile}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              Load more
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default GifPickerDialog;
